import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { FormValues, StepPayment } from '../../../common/components/StepPayment';
import { Trans } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DateTime } from 'luxon';
import {
  GqlCompany,
  GqlPaymentAccountKind,
  GqlShareIssue,
  GqlShareIssueRegistration,
} from '../../../../graphql/base-types.graphql';
import { useSirSetPaymentMutation } from '../../../../graphql/react-operations';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { captureException } from '@sentry/react';
import { toast } from '@startuptools/ui/toast/use-toast';
import { Alert } from '@startuptools/ui/Alert';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { toDateTimeMaybe } from '@startuptools/common/common';

export const SiStepPayment = ({
  company,
  shareIssueRegistration: {
    id: sirId,
    paymentDueDate: payBeforeDate,
    paymentAccountKind,
    paymentAccountNumber,
    paymentName,
    ibanNumber,
    ibanBank,
    ibanBic,
    ibanAddress,
  },
  shareIssue,
}: {
  company: Pick<GqlCompany, 'id' | 'organizationNumber'>;
  shareIssueRegistration: Pick<
    GqlShareIssueRegistration,
    | 'id'
    | 'paymentDueDate'
    | 'paymentAccountKind'
    | 'paymentAccountNumber'
    | 'paymentName'
    | 'ibanNumber'
    | 'ibanBank'
    | 'ibanBic'
    | 'ibanAddress'
  >;
  shareIssue: Pick<GqlShareIssue, 'id' | 'subscriptionEndDate'>;
}) => {
  const { stepNext } = useStepper();

  const [doSubmit, { loading }] = useSirSetPaymentMutation({
    refetchQueries: [ShareIssueDocument],
    awaitRefetchQueries: true,
  });

  const { control, register, watch, handleSubmit, setValue, reset } = useForm<FormValues>({
    defaultValues: {
      payBeforeDate:
        toDateTimeMaybe(payBeforeDate) ?? toDateTimeMaybe(shareIssue.subscriptionEndDate) ?? DateTime.now(),
      paymentAccountKind: paymentAccountKind ?? GqlPaymentAccountKind.Other,
      paymentAccountNumber: paymentAccountNumber ?? '',
      paymentName: paymentName ?? '',
      ibanNumber: ibanNumber ?? '',
      ibanBank: ibanBank ?? '',
      ibanAddress: ibanAddress ?? '',
      ibanBic: ibanBic ?? '',
    },
  });

  const onSubmit: SubmitHandler<FormValues> = async ({ payBeforeDate, ...values }) => {
    try {
      const res = await doSubmit({
        variables: {
          companyId: company.id,
          input: { id: sirId, paymentDueDate: payBeforeDate.toUTC(0, { keepLocalTime: true }).toString(), ...values },
        },
      });
      const sir = res.data?.sirSetPayment.sir;
      if (sir) {
        reset({
          payBeforeDate: sir.paymentDueDate ? DateTime.fromISO(sir.paymentDueDate) : DateTime.now(),
          paymentAccountKind: sir.paymentAccountKind ?? GqlPaymentAccountKind.Other,
          paymentAccountNumber: sir.paymentAccountNumber ?? '',
          paymentName: sir.paymentName ?? '',
          ibanNumber: sir.ibanNumber ?? '',
          ibanBank: sir.ibanBank ?? '',
          ibanAddress: sir.ibanAddress ?? '',
          ibanBic: sir.ibanBic ?? '',
        });
      }
      return stepNext();
    } catch (e) {
      captureException(e);
      toast({
        title: <Trans id="Could not save payment information" />,
        description: (
          <Trans id="Could not save payment information, try again or contact support if problem persists" />
        ),
        severity: 'danger',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap={3}>
        <Typography level="h4">
          <Trans id="Payment Accounts" />
        </Typography>

        <Alert>
          <Trans id="The account the investors pay to should be empty when the share issue begins. This makes it easier for the bank to see which money is tied to the share issue, which they need to keep track of for issuing the bank statement." />
        </Alert>

        <StepPayment
          control={control}
          register={register}
          watch={watch}
          setValue={setValue}
          company={company}
          minDate={{
            date: shareIssue.subscriptionEndDate
              ? DateTime.fromISO(shareIssue.subscriptionEndDate).startOf('day')
              : DateTime.now().startOf('day'),
            severity: 'error',
          }}
        />

        <Box>
          <Button type="submit" loading={loading}>
            <Trans id="Save and Continue" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
